<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  reactive,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import { companyListContext } from 'dashboard/components-next/Companies/companyNavigation';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import { useCompaniesStore } from 'dashboard/stores/companies';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CompaniesListLayout from 'dashboard/components-next/Companies/CompaniesListLayout.vue';
import CompaniesCard from 'dashboard/components-next/Companies/CompaniesCard/CompaniesCard.vue';
import CompanyCreateDialog from 'dashboard/components-next/Companies/CompanyCreateDialog.vue';

const DEFAULT_SORT_FIELD = 'name';
const DEBOUNCE_DELAY = 300;

const companiesStore = useCompaniesStore();

const route = useRoute();
const { isWorkspaceV2 } = useWorkspaceV2();
const router = useRouter();
const { t } = useI18n();

const { updateUISettings, uiSettings } = useUISettings();

const companies = computed(() => companiesStore.getCompaniesList);
const meta = computed(() => companiesStore.getMeta);
const uiFlags = computed(() => companiesStore.getUIFlags);

const searchQuery = computed(() => route.query?.search || '');
const searchValue = ref(searchQuery.value);
const createCompanyDialogRef = ref(null);
const submittingCompany = ref(false);
const pageNumber = computed(() => Number(route.query?.page) || 1);

const parseSortSettings = (sortString = '') => {
  const hasDescending = sortString.startsWith('-');
  const sortField = hasDescending ? sortString.slice(1) : sortString;
  return {
    sort: sortField || DEFAULT_SORT_FIELD,
    order: hasDescending ? '-' : '',
  };
};

const { companies_sort_by: companySortBy = DEFAULT_SORT_FIELD } =
  uiSettings.value ?? {};
const { sort: initialSort, order: initialOrder } = parseSortSettings(
  route.query.sort || companySortBy
);

const sortState = reactive({
  activeSort: initialSort,
  activeOrdering: initialOrder,
});

const activeSort = computed(() => sortState.activeSort);
const activeOrdering = computed(() => sortState.activeOrdering);

const isFetchingList = computed(() => uiFlags.value.fetchingList);
const isCreatingCompany = computed(() => uiFlags.value.creatingItem);

const buildSortAttr = () =>
  `${sortState.activeOrdering}${sortState.activeSort}`;

const sortParam = computed(() => buildSortAttr());

const updateURLParams = (page, search = '', sort = '') => {
  const query = {
    ...route.query,
    page: page.toString(),
  };

  if (search) {
    query.search = search;
  } else {
    delete query.search;
  }

  if (sort) {
    query.sort = sort;
  } else {
    delete query.sort;
  }

  router.replace({ query });
};

const fetchCompanies = async (page, search, sort) => {
  const currentPage = page ?? pageNumber.value;
  const currentSearch = search ?? searchQuery.value;
  const currentSort = sort ?? sortParam.value;

  // Only update URL if arguments were explicitly provided
  if (page !== undefined || search !== undefined || sort !== undefined) {
    updateURLParams(currentPage, currentSearch, currentSort);
  }

  try {
    await companiesStore.fetchCompanyList({
      search: currentSearch,
      page: currentPage,
      sort: currentSort,
    });
  } catch {
    // The store exposes the current request error for inline recovery.
  }
};

let active = true;
const onSearch = debounce(query => {
  if (!active) return;
  searchValue.value = query;
  fetchCompanies(1, query, sortParam.value);
}, DEBOUNCE_DELAY);

// SiteUp: busca sem resultado oferece limpar a busca; lista vazia sem busca
// é o primeiro uso e oferece criar a empresa.
const isSearchEmpty = computed(() => Boolean(searchValue.value));
const clearSearch = () => {
  searchValue.value = '';
  fetchCompanies(1, '', sortParam.value);
};

const onPageChange = page => {
  fetchCompanies(page, searchValue.value, sortParam.value);
};

const showCompany = companyId => {
  router.push({
    name: 'companies_dashboard_show',
    ...(isWorkspaceV2.value
      ? { query: companyListContext({ ...route.query, sort: sortParam.value }) }
      : {}),
    params: {
      accountId: route.params.accountId,
      companyId,
    },
  });
};

const openCreateCompanyDialog = () => {
  createCompanyDialogRef.value?.open();
};

const createCompany = async company => {
  if (submittingCompany.value || isCreatingCompany.value) return;
  submittingCompany.value = true;
  try {
    const newCompany = await companiesStore.create(company);
    createCompanyDialogRef.value?.onSuccess();
    useAlert(t('COMPANIES.CREATE.MESSAGES.SUCCESS'));
    showCompany(newCompany.id);
  } catch {
    const message = t('COMPANIES.CREATE.MESSAGES.ERROR');
    createCompanyDialogRef.value?.onError(message);
    useAlert(message);
  } finally {
    submittingCompany.value = false;
  }
};

const handleSort = async ({ sort, order }) => {
  Object.assign(sortState, { activeSort: sort, activeOrdering: order });

  await updateUISettings({
    companies_sort_by: buildSortAttr(),
  });

  fetchCompanies(1, searchValue.value, buildSortAttr());
};

onBeforeUnmount(() => {
  active = false;
  companiesStore.invalidateCompanyList();
});
watch(
  () => route.params.accountId,
  () => {
    companiesStore.records = [];
    companiesStore.meta = {};
    fetchCompanies();
  }
);

onMounted(() => {
  searchValue.value = searchQuery.value;

  if (!route.query.sort && sortParam.value !== DEFAULT_SORT_FIELD) {
    updateURLParams(pageNumber.value, searchQuery.value, sortParam.value);
  }

  fetchCompanies();
});
</script>

<template>
  <CompaniesListLayout
    data-testid="page-companies"
    :search-value="searchValue"
    :header-title="t('COMPANIES.HEADER')"
    :current-page="pageNumber"
    :total-items="Number(meta.totalCount || 0)"
    :active-sort="activeSort"
    :active-ordering="activeOrdering"
    :is-fetching-list="isFetchingList"
    :show-pagination-footer="!!companies.length && !companiesStore.listError"
    @update:current-page="onPageChange"
    @update:sort="handleSort"
    @search="onSearch"
    @create="openCreateCompanyDialog"
  >
    <div
      v-if="companiesStore.listError"
      role="alert"
      class="space-y-3 rounded-xl border border-n-weak p-4 text-sm text-n-ruby-11"
    >
      <p>{{ t('WORKSPACE_V2.COMPANIES_LIST.LOAD_ERROR') }}</p>
      <Button
        :label="t('WORKSPACE_V2.COMPANIES_LIST.RETRY')"
        class="min-h-11 !rounded-xl"
        @click="fetchCompanies()"
      />
    </div>
    <div
      v-else-if="isFetchingList"
      role="status"
      class="flex min-h-48 items-center justify-center p-8 text-n-slate-11"
    >
      <Spinner />
      <span class="sr-only">{{ t('COMPANIES.LOADING') }}</span>
    </div>
    <div
      v-else-if="companies.length === 0 && isSearchEmpty"
      data-testid="companies-search-empty"
      class="flex min-h-48 flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-n-weak p-8 text-center"
    >
      <p class="m-0 text-base text-n-slate-12">
        {{ t('COMPANIES.EMPTY_STATE.SEARCH_TITLE') }}
      </p>
      <Button
        :label="t('COMPANIES.EMPTY_STATE.CLEAR_SEARCH')"
        color="slate"
        variant="faded"
        class="!min-h-11 !rounded-xl"
        @click="clearSearch"
      />
    </div>
    <div
      v-else-if="companies.length === 0"
      data-testid="companies-first-use"
      class="flex min-h-48 flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-n-weak p-8 text-center"
    >
      <p class="m-0 text-base font-medium text-n-slate-12">
        {{ t('COMPANIES.EMPTY_STATE.TITLE') }}
      </p>
      <p class="m-0 max-w-sm text-sm text-n-slate-11">
        {{ t('COMPANIES.EMPTY_STATE.SUBTITLE') }}
      </p>
      <Button
        :label="t('COMPANIES.EMPTY_STATE.CREATE')"
        icon="i-ph-plus"
        class="!min-h-11 !rounded-xl"
        @click="openCreateCompanyDialog"
      />
    </div>
    <div v-else class="flex flex-col gap-4">
      <CompaniesCard
        v-for="company in companies"
        :id="company.id"
        :key="company.id"
        :name="company.name"
        :domain="company.domain"
        :contacts-count="company.contactsCount || 0"
        :avatar-url="company.avatarUrl"
        :last-activity-at="company.lastActivityAt"
        @show-company="showCompany"
      />
    </div>
    <CompanyCreateDialog
      ref="createCompanyDialogRef"
      :is-loading="isCreatingCompany || submittingCompany"
      @create="createCompany"
    />
  </CompaniesListLayout>
</template>
